import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { partners as partnersApi } from '../api/endpoints';
import { useOrderEvents, useSocket } from '../context/SocketContext';
import { Loading, Empty, StatusBadge } from '../components/Common';

/**
 * Live delivery monitoring.
 *
 * Riders stream GPS over the socket; the panel plots them on a schematic grid.
 * The grid is deliberately provider-agnostic — swapping in Google Maps means
 * replacing `<MapCanvas>` and keeping the same `partners` state, since the
 * positions already arrive as plain lat/lng.
 */
export default function LiveMap() {
  const { connected } = useSocket();
  const [partners, setPartners] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await partnersApi.live();
      setPartners(res.data.partners);
      setActiveOrders(res.data.activeOrders);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Live Map · UrbanClean'; }, []);

  // Move a pin in place rather than refetching the whole list on every ping.
  useOrderEvents((event, payload) => {
    if (event === 'partner:location') {
      setPartners((list) =>
        list.map((p) =>
          String(p._id) === String(payload.partnerId)
            ? { ...p, currentLocation: { lat: payload.lat, lng: payload.lng, updatedAt: payload.at } }
            : p
        )
      );
    }
    if (event === 'partner:status' || event === 'order:updated') load();
  });

  if (loading) return <Loading label="Loading live map" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Live Map</h1>
          <div className="sub">
            {partners.length} partner(s) online · {activeOrders.length} active trip(s)
            {connected ? '' : ' · reconnecting…'}
          </div>
        </div>
        <button className="btn btn-ghost" onClick={load}>Refresh</button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(280px, 1fr)' }}>
        <div className="card">
          <div className="card-head"><h3>Partner positions</h3></div>
          <div className="card-body">
            <MapCanvas partners={partners} selected={selected} onSelect={setSelected} />
            <div className="hint" style={{ marginTop: 10 }}>
              Positions update live over the socket as riders move. Plug in Google Maps by
              replacing this canvas — the coordinates are already real lat/lng.
            </div>
          </div>
        </div>

        <div>
          <div className="card mb">
            <div className="card-head"><h3>Online partners</h3></div>
            {partners.length === 0 ? (
              <Empty title="No one is online" body="Riders appear here when they go on duty." />
            ) : (
              <div>
                {partners.map((p) => (
                  <button
                    key={p._id}
                    className="tray-item"
                    onClick={() => setSelected(p._id)}
                    style={{ background: selected === p._id ? 'var(--blue-50)' : 'transparent' }}
                  >
                    <div>
                      <div className="strong">{p.name || p.mobile}</div>
                      <div className="muted" style={{ fontSize: 11.5 }}>
                        {p.vehicle?.type} {p.vehicle?.number}
                        {p.currentLocation?.lat
                          ? ` · ${p.currentLocation.lat.toFixed(3)}, ${p.currentLocation.lng.toFixed(3)}`
                          : ' · no GPS yet'}
                      </div>
                    </div>
                    <span className="badge badge-green">Online</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-head"><h3>Trips in progress</h3></div>
            {activeOrders.length === 0 ? (
              <Empty title="No active trips" />
            ) : (
              <div>
                {activeOrders.map((o) => (
                  <Link key={o._id} to={`/orders/${o._id}`} className="tray-item" style={{ display: 'flex' }}>
                    <div>
                      <div className="strong mono">{o.orderNumber}</div>
                      <div className="muted" style={{ fontSize: 11.5 }}>
                        {o.deliveryAddress?.city || o.pickupAddress?.city || '—'}
                      </div>
                    </div>
                    <StatusBadge status={o.status} />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Projects lat/lng onto the canvas. Bounds are derived from the partners in
 * view, with a fallback box around Delhi so a single rider still renders sanely.
 */
function MapCanvas({ partners, selected, onSelect }) {
  const points = partners.filter((p) => p.currentLocation?.lat != null);

  if (points.length === 0) {
    return (
      <div className="map-canvas">
        <div className="empty" style={{ paddingTop: 190 }}>
          Waiting for GPS from riders on duty…
        </div>
      </div>
    );
  }

  const lats = points.map((p) => p.currentLocation.lat);
  const lngs = points.map((p) => p.currentLocation.lng);
  const pad = 0.02;
  const minLat = Math.min(...lats) - pad;
  const maxLat = Math.max(...lats) + pad;
  const minLng = Math.min(...lngs) - pad;
  const maxLng = Math.max(...lngs) + pad;

  const project = (lat, lng) => ({
    left: `${((lng - minLng) / (maxLng - minLng || 1)) * 100}%`,
    // Latitude grows northwards, the canvas grows downwards.
    top: `${(1 - (lat - minLat) / (maxLat - minLat || 1)) * 100}%`,
  });

  return (
    <div className="map-canvas">
      {points.map((p) => {
        const pos = project(p.currentLocation.lat, p.currentLocation.lng);
        const isSelected = selected === p._id;
        return (
          <div
            key={p._id}
            className="map-pin"
            style={{ ...pos, zIndex: isSelected ? 10 : 1 }}
            onClick={() => onSelect(p._id)}
          >
            <div
              className="pin-dot"
              style={{ background: isSelected ? 'var(--navy)' : 'var(--blue)', transform: isSelected ? 'scale(1.25)' : 'none' }}
            />
            <div className="pin-label">{p.name || p.mobile}</div>
          </div>
        );
      })}
    </div>
  );
}
